import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Send, Loader2, MessageSquare, Users, FileText, LayoutGrid, Plus, Radio, ShieldCheck, DollarSign, MessagesSquare, ArrowDownLeft,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  commsOverview, commsTemplates, commsUpsertTemplate, commsContacts, commsUpsertContact,
  commsRecordConsent, commsSend, commsMessages, commsSentSettings, commsSetSentSettings, commsSimulateInbound,
} from "@/lib/api";

const CHANNELS = ["email", "sms", "whatsapp"];
const CLASSES = ["authentication", "transactional", "operational", "reminder", "emergency", "marketing"];
const STATE_STYLE = {
  submitted: "bg-blue-50 text-blue-700", sent: "bg-indigo-50 text-indigo-700", delivered: "bg-emerald-50 text-emerald-700",
  read: "bg-emerald-100 text-emerald-800", queued: "bg-amber-50 text-amber-700", blocked: "bg-red-50 text-red-600",
  failed: "bg-red-50 text-red-600", dead_letter: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500",
  received: "bg-violet-50 text-violet-700",
};
const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
// Matches backend SENT_EST_PRICE — a spend preview before you send (real prices reconcile later).
const EST_PRICE = { sms: 0.0079, whatsapp: 0.005 };

function CostPreview({ channel, count = 1 }) {
  if (channel === "email") {
    return <div className="text-xs text-slate-500 rounded-lg bg-slate-50 px-3 py-2" data-testid="send-cost-preview">Email is included — no per-message charge.</div>;
  }
  const unit = EST_PRICE[channel] || 0;
  const total = unit * count;
  return (
    <div className="text-xs text-slate-600 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2" data-testid="send-cost-preview">
      ≈ <strong>${unit.toFixed(4)}</strong> per {channel} · {count} recipient{count === 1 ? "" : "s"} ≈ <strong data-testid="send-cost-total">${total.toFixed(4)}</strong>
      <span className="text-slate-400"> (estimate)</span>
    </div>
  );
}

export default function CommunicationCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  if (user === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (user === null) return (
    <div className="min-h-screen flex flex-col"><Navbar />
      <div className="flex-1 grid place-items-center px-6">
        <div className="max-w-md text-center" data-testid="comms-login-gate">
          <Radio className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Communication Center</h1>
          <p className="text-slate-600 mb-6">Sign in to send and track email, SMS and WhatsApp — with built-in consent, templates and delivery tracking.</p>
          <Link to="/login" data-testid="comms-login-link" className="inline-block px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">Sign in to continue</Link>
        </div>
      </div><Footer />
    </div>
  );

  const tabs = [["overview", "Overview", LayoutGrid], ["send", "Send", Send], ["templates", "Templates", FileText], ["contacts", "Contacts & Consent", Users], ["messages", "Messages", MessageSquare]];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50"><Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 pt-28 pb-12" data-testid="comms-dashboard">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Communication Center</h1>
          <p className="text-slate-600 mt-1 text-sm">Email, SMS &amp; WhatsApp — consent-first, tracked, and ready for Relay.</p>
        </div>
        <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`comms-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "send" && <SendTab />}
        {tab === "templates" && <TemplatesTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "messages" && <MessagesTab />}
      </main><Footer />
    </div>
  );
}

function Overview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { commsOverview().then(setD).catch(() => setD(null)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) commsSentSettings().then(setSent).catch(() => setSent(null)); }, [isAdmin]);
  const toggleSent = async (live) => {
    setBusy(true);
    try { const r = await commsSetSentSettings({ live }); setSent((s) => ({ ...s, live: r.live })); load(); }
    finally { setBusy(false); }
  };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const tiles = [["Total messages", d.total], ["Delivered", (d.by_status.delivered || 0) + (d.by_status.read || 0)], ["Blocked by consent", d.by_status.blocked || 0], ["Failed", (d.by_status.failed || 0) + (d.by_status.dead_letter || 0)], ["Contacts", d.contacts], ["Templates", d.templates]];
  const spend = d.spend || { actual: 0, estimated: 0, by_channel: {} };
  const money = (n) => `$${(n || 0).toFixed(4)}`;
  return (
    <div className="space-y-5" data-testid="comms-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-slate-900">{v}</div><div className="text-xs text-slate-500 mt-0.5">{l}</div></div>
        ))}
      </div>

      {/* SMS/WhatsApp spend tracker */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="comms-spend">
        <div className="flex items-center gap-2 mb-3"><DollarSign className="w-4 h-4 text-emerald-600" /><span className="font-semibold text-slate-800">SMS &amp; WhatsApp spend</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{d.sent_live ? "live prices" : "estimated"}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><div className="text-2xl font-bold text-slate-900" data-testid="spend-estimated">{money(spend.estimated)}</div><div className="text-xs text-slate-500">Estimated total</div></div>
          <div><div className="text-2xl font-bold text-emerald-600" data-testid="spend-actual">{money(spend.actual)}</div><div className="text-xs text-slate-500">Actual (Sent.dm)</div></div>
          <div><div className="text-lg font-semibold text-slate-700">{money(spend.by_channel?.sms)}</div><div className="text-xs text-slate-500">SMS</div></div>
          <div><div className="text-lg font-semibold text-slate-700">{money(spend.by_channel?.whatsapp)}</div><div className="text-xs text-slate-500">WhatsApp</div></div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Estimates shown until Sent.dm reports the authoritative per-message price. Real charges only occur when the channel is live.</p>
      </div>

      {/* Provider status + admin on/off toggle */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="comms-provider">
        <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> SMS/WhatsApp provider: <strong>{d.sent_provider}</strong></span>
        {isAdmin && sent && (
          <div className="flex items-center gap-3">
            {sent.month_spend_global && (
              <span className="text-xs text-slate-500" data-testid="platform-month-spend">
                Platform spend this month: <strong className="text-slate-800">${(sent.month_spend_global.estimated || 0).toFixed(4)}</strong>
              </span>
            )}
            {!sent.key_configured && <span className="text-[11px] text-amber-600">Add SENT_API_KEY to send for real</span>}
            <span className="text-xs text-slate-500">Sent.dm channel</span>
            <button
              data-testid="sent-toggle"
              disabled={busy}
              onClick={() => toggleSent(!sent.live)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sent.live ? "bg-emerald-500" : "bg-slate-300"} ${busy ? "opacity-60" : ""}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${sent.live ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <span className={`text-xs font-semibold ${sent.live ? "text-emerald-600" : "text-slate-500"}`} data-testid="sent-state">{sent.live ? "ON" : "OFF"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SendTab() {
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ contact_id: "", channel: "sms", template_key: "", locale: "en", variables: "{}" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    commsTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
    commsContacts().then((r) => setContacts(r.contacts || [])).catch(() => {});
  }, []);
  const send = async () => {
    setErr(""); setResult(null);
    let vars = {};
    try { vars = JSON.parse(form.variables || "{}"); } catch { setErr("Variables must be valid JSON."); return; }
    if (!form.contact_id || !form.template_key) { setErr("Pick a contact and a template."); return; }
    setBusy(true);
    try { setResult(await commsSend({ ...form, variables: vars })); }
    catch (e) { setErr(e?.response?.data?.detail || "Send failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="comms-send">
      <div><label className="text-xs font-semibold text-slate-500 uppercase">Contact</label>
        <select className={field} value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} data-testid="send-contact">
          <option value="">Choose a contact</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name || c.email || c.phone}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <div className="flex-1"><label className="text-xs font-semibold text-slate-500 uppercase">Channel</label>
          <select className={field} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="send-channel">{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="flex-1"><label className="text-xs font-semibold text-slate-500 uppercase">Template</label>
          <select className={field} value={form.template_key} onChange={(e) => setForm({ ...form, template_key: e.target.value })} data-testid="send-template">
            <option value="">Choose</option>{templates.map((t) => <option key={t.key} value={t.key}>{t.key} ({t.classification})</option>)}</select></div>
      </div>
      <div><label className="text-xs font-semibold text-slate-500 uppercase">Variables (JSON)</label>
        <textarea rows={3} className={field + " font-mono text-xs"} value={form.variables} onChange={(e) => setForm({ ...form, variables: e.target.value })} data-testid="send-vars" /></div>
      <CostPreview channel={form.channel} count={1} />
      {err && <div className="text-xs text-red-600" data-testid="send-error">{err}</div>}
      <button onClick={send} disabled={busy} data-testid="send-btn" className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Queue message</button>
      {result && <div className={`text-xs rounded-lg px-3 py-2 ${result.status === "blocked" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`} data-testid="send-result">Status: <strong>{result.status}</strong>{result.reason ? ` — ${result.reason}` : ""}</div>}
    </div>
  );
}

function TemplatesTab() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ key: "", classification: "transactional", subject: "", text: "", provider_template: "" });
  const [msg, setMsg] = useState("");
  const load = useCallback(() => commsTemplates().then((r) => setList(r.templates || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!f.key.trim()) return;
    await commsUpsertTemplate({ key: f.key.trim(), classification: f.classification, default_locale: "en",
      provider_template: f.provider_template.trim(),
      locales: { en: { email: { subject: f.subject, html: f.text ? `<p>${f.text}</p>` : "" }, sms: { text: f.text }, whatsapp: { text: f.text } } } });
    setMsg("Saved"); setF({ key: "", classification: "transactional", subject: "", text: "", provider_template: "" }); load(); setTimeout(() => setMsg(""), 2000);
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="comms-templates">
      <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-6 h-fit">
        <div className="font-semibold text-slate-800 flex items-center gap-1.5"><Plus className="w-4 h-4" /> New / update template</div>
        <input className={field} placeholder="key (e.g. appt_reminder)" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} data-testid="tpl-key" />
        <select className={field} value={f.classification} onChange={(e) => setF({ ...f, classification: e.target.value })} data-testid="tpl-class">{CLASSES.map((c) => <option key={c}>{c}</option>)}</select>
        <input className={field} placeholder="Email subject (supports {{vars}})" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="tpl-subject" />
        <textarea rows={3} className={field} placeholder="Message body (supports {{vars}})" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="tpl-text" />
        <input className={field} placeholder="Sent.dm template name (SMS/WhatsApp, optional)" value={f.provider_template} onChange={(e) => setF({ ...f, provider_template: e.target.value })} data-testid="tpl-provider" />
        <p className="text-[11px] text-slate-400 -mt-1">For SMS/WhatsApp, this must match the template name registered in your Sent.dm dashboard.</p>
        <button onClick={save} data-testid="tpl-save" className="w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Save template</button>
        {msg && <div className="text-xs text-emerald-600">{msg}</div>}
      </div>
      <div className="space-y-2" data-testid="tpl-list">
        {list.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No templates yet.</div> :
          list.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`tpl-${t.key}`}>
              <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{t.key}</span><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.classification} · v{t.version}</span></div>
              <div className="text-xs text-slate-500 mt-1">Locales: {Object.keys(t.locales || {}).join(", ") || "—"} · {t.state}{t.provider_template ? ` · Sent.dm: ${t.provider_template}` : ""}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

function ContactsTab() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const load = useCallback(() => commsContacts().then((r) => setList(r.contacts || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!f.email && !f.phone) return; await commsUpsertContact(f); setF({ name: "", email: "", phone: "" }); load(); };
  const consent = async (cid, channel, purpose, status) => { await commsRecordConsent({ contact_id: cid, channel, purpose, status }); };
  return (
    <div className="space-y-4" data-testid="comms-contacts">
      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input className={field + " flex-1 min-w-[120px]"} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ct-name" />
        <input className={field + " flex-1 min-w-[160px]"} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ct-email" />
        <input className={field + " flex-1 min-w-[140px]"} placeholder="+15551234567" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="ct-phone" />
        <button onClick={add} data-testid="ct-add" className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Add contact</button>
      </div>
      <div className="space-y-2">
        {list.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ct-${c.id}`}>
            <div className="font-semibold text-slate-900">{c.name || "—"}</div>
            <div className="text-xs text-slate-500">{c.email || ""} {c.phone || ""}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {CHANNELS.flatMap((ch) => ["transactional", "promotional"].map((p) => (
                <button key={ch + p} onClick={() => consent(c.id, ch, p, "granted")} data-testid={`consent-${c.id}-${ch}-${p}`}
                  className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-600">Grant {ch}/{p[0]}</button>
              )))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InboundSimulator({ onSent }) {
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (preset) => {
    const body = { from_phone: phone.trim(), text: (preset ?? text).trim() };
    if (!body.from_phone || !body.text) { setRes({ error: "Enter a phone and a message." }); return; }
    setBusy(true);
    try { const r = await commsSimulateInbound(body); setRes(r); onSent?.(); }
    catch (e) { setRes({ error: e?.response?.data?.detail || "Failed" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 space-y-2" data-testid="inbound-simulator">
      <div className="flex items-center gap-2 text-sm font-semibold text-violet-800"><MessagesSquare className="w-4 h-4" /> Test two-way replies</div>
      <p className="text-[11px] text-violet-700/80">Simulate a customer texting back (works on the mock — no live number needed). Try a keyword or free text.</p>
      <div className="flex flex-wrap gap-2">
        <input className={field + " flex-1 min-w-[150px]"} placeholder="+15551234567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="sim-phone" />
        <input className={field + " flex-1 min-w-[180px]"} placeholder="Message (e.g. CANCEL)" value={text} onChange={(e) => setText(e.target.value)} data-testid="sim-text" />
        <button onClick={() => run()} disabled={busy} data-testid="sim-send" className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50">{busy ? "Sending…" : "Send reply"}</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {["CONFIRM", "RESCHEDULE", "CANCEL", "STOP", "START", "HELP"].map((k) => (
          <button key={k} onClick={() => run(k)} disabled={busy} data-testid={`sim-preset-${k}`} className="text-[11px] px-2 py-1 rounded-full border border-violet-300 text-violet-700 hover:bg-violet-100">{k}</button>
        ))}
      </div>
      {res && (
        <div className="text-xs rounded-lg bg-white border border-slate-200 px-3 py-2" data-testid="sim-result">
          {res.error ? <span className="text-red-600">{res.error}</span> :
            <span><strong className="text-violet-700">{res.action || res.reason || (res.handled ? "handled" : "not handled")}</strong>{res.reply_text ? <> — Ava replied: "{res.reply_text}"</> : (res.action === "opted_out" ? " — (no bot reply; Sent.dm sends the opt-out confirmation)" : "")}</span>}
        </div>
      )}
    </div>
  );
}

function MessagesTab() {
  const [list, setList] = useState(null);
  const [month, setMonth] = useState(null);
  const [filter, setFilter] = useState("");
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    commsMessages(filter ? { status: filter } : {})
      .then((r) => { setList(r.messages || []); setMonth(r.month_spend || null); })
      .catch(() => { setList([]); setMonth(null); });
  }, [filter, nonce]);
  const money = (n) => `$${(n || 0).toFixed(4)}`;
  const cost = (m) => {
    if (m.channel === "email" || m.direction === "inbound") return null;
    const billed = m.price != null;
    const val = billed ? m.price : (m.price_est || 0);
    return (
      <div className="shrink-0 text-right" data-testid={`msg-cost-${m.id}`}>
        <div className={`text-sm font-semibold ${billed ? "text-emerald-600" : "text-slate-700"}`}>{money(val)}</div>
        <div className="text-[10px] uppercase tracking-wide text-slate-400">{billed ? "billed" : "est"}</div>
      </div>
    );
  };
  if (list === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-3" data-testid="comms-messages">
      <InboundSimulator onSent={() => setNonce((n) => n + 1)} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select className={field + " max-w-[200px]"} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="msg-filter">
          <option value="">All statuses</option>{["submitted", "sent", "delivered", "received", "blocked", "failed", "queued", "dead_letter"].map((s) => <option key={s}>{s}</option>)}
        </select>
        {month && (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="msg-month-spend">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="text-slate-500">SMS/WhatsApp this month:</span>
            <strong className="text-slate-900" data-testid="msg-month-estimated">{money(month.estimated)}</strong>
            <span className="text-xs text-slate-400">({money(month.actual)} billed · since {month.since})</span>
          </div>
        )}
      </div>
      {list.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No messages.</div> :
        list.map((m) => (
          <div key={m.id} className={`rounded-xl border p-4 flex items-center justify-between gap-3 ${m.direction === "inbound" ? "border-violet-200 bg-violet-50/50" : "border-slate-200 bg-white"}`} data-testid={`msg-${m.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                {m.direction === "inbound" && <ArrowDownLeft className="w-3.5 h-3.5 text-violet-600" />}
                {m.channel} · {m.destination}{m.direction === "inbound" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">reply</span>}
              </div>
              <div className="text-xs text-slate-500 truncate max-w-md">{m.subject ? `${m.subject} — ` : ""}{m.body}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{m.classification} · {new Date(m.created_at).toLocaleString()}{m.error ? ` · ${m.error}` : ""}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {cost(m)}
              <span className={`text-xs px-2 py-1 rounded-full ${STATE_STYLE[m.status] || "bg-slate-100 text-slate-600"}`}>{m.status}</span>
            </div>
          </div>
        ))}
    </div>
  );
}
